import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { Main, Bottom, FormButton, Form, Icon, DropTab, Titles, Description } from './styles';
import Button from '../../button';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { addCustomerMember, getCustomerMembersList, updateCustomerMember } from '../../../services/customerServices';
import { isEmpty } from '../../../helpers/common';
import { BUTTON_TYPE, CUSTOMER_MEMBER_ROLE, ICON_SIZE, roleOptions } from '../../../global/constants';
import { getWorkspaceDetails } from '../../../services/workspaceService';
import { Dropdown, DropdownItem } from '../../dropdown';
import { rootStore } from '../../../mobx/rootStore';
import ModalHeader from '../modalHeader';
import Input from '../../input';

interface Props {
  closeModal: (item?: string) => void;
  customer_id: string;
}

const AddMemberModal = (props: Props) => {
  const { closeModal, customer_id } = props;
  //States
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  //Mobx store variables
  const { workspaceStore, customerStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { memberInput, loading } = customerStore;
  const { name, role, title, email } = memberInput || {};

  const checkUserAccess = useCallback(() => {
    if (memberInput?.id) {
      if (
        memberInput?.role === CUSTOMER_MEMBER_ROLE?.FULL_ACCESS ||
        memberInput?.role === CUSTOMER_MEMBER_ROLE?.PROJECT_ONLY
      ) {
        const userInWorkspace = workspace?.users?.find((user) => user?.id === memberInput?.id);
        if (userInWorkspace) setIsDisabled(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberInput?.id, workspace?.users]);

  useEffect(() => {
    checkUserAccess();
  }, [checkUserAccess]);

  useEffect(() => {
    return () => {
      customerStore.clearMemberInput();
    };
  }, [customerStore]);

  const handleChange = useCallback(
    (propsName: string, value: string | number) => {
      customerStore.updateMemberInput(propsName, value);
    },
    [customerStore]
  );

  const getCurrentSelectedRoleIcon = useMemo(() => {
    switch (role) {
      case 1:
        return (
          <SVGIcon
            name='no-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            fill='var(--text-secondary)'
          />
        );
      case 2:
        return (
          <Icon>
            <SVGIcon
              name='project-icon'
              width={ICON_SIZE.MD}
              height={ICON_SIZE.MD}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              className='fill-colors'
            />
          </Icon>
        );
      case 3:
        return (
          <Icon>
            <SVGIcon
              name='access-icon'
              width={ICON_SIZE.MD}
              height={ICON_SIZE.MD}
              viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
              className='fill-colors'
            />
          </Icon>
        );
      default:
        return (
          <Icon>
            <SVGIcon
              name='lock-icon'
              width={ICON_SIZE.MD}
              height={ICON_SIZE.MD}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              fill='var(--text-secondary)'
            />
          </Icon>
        );
    }
  }, [role]);

  const getRoleText = useCallback(() => {
    switch (role) {
      case 1:
        return (
          <Description>
            <h2>No Access</h2>
          </Description>
        );
      case 2:
        return (
          <Description>
            <h2>Project Only</h2>
          </Description>
        );
      case 3:
        return (
          <Description>
            <h2>Full Access</h2>
          </Description>
        );
      default:
        return 'Access Control';
    }
  }, [role]);

  const onSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      let response;
      if (!isEmpty(memberInput?.id)) {
        response = await updateCustomerMember(memberInput, customer_id);
      } else {
        response = await addCustomerMember(memberInput, customer_id);
      }
      if (response) {
        if (memberInput?.role !== CUSTOMER_MEMBER_ROLE.NO_ACCESS) {
          await Promise.all([
            getWorkspaceDetails(workspace?.id || '', { force: true }),
            getCustomerMembersList(customer_id)
          ]);
        } else await getCustomerMembersList(customer_id);
        closeModal(memberInput?.role !== CUSTOMER_MEMBER_ROLE.NO_ACCESS ? response?.RedirectUri : undefined);
      }
    },
    [closeModal, customer_id, memberInput, workspace?.id]
  );

  const renderRoleOptions = useMemo(
    () =>
      roleOptions.map((item) => (
        <DropdownItem
          key={item.key}
          iconName={item.iconName}
          iconSize={16}
          label={item.label}
          description={item.description}
          onClick={() => handleChange('role', item.roleValue)}
          align='flex-start'
          iconViewBox={item.label === 'Full Access' ? '0 0 16 16' : '0 0 18 18'}
        />
      )),
    [handleChange]
  );
  return (
    <Main onSubmit={onSubmit}>
      <ModalHeader title={isEmpty(memberInput?.id) ? 'Add new member' : 'Edit member'} onClose={() => closeModal()} />
      <Bottom>
        <Form>
          <Input
            type='text'
            label='Name'
            placeHolder='Enter member name'
            value={name}
            onChange={(e) => handleChange('name', e.target.value)}
            isDisabled={isDisabled}
          />
          <Input
            type='text'
            label='Role'
            placeHolder='e.g., CEO, Project Manager, Finance'
            value={title}
            onChange={(e) => handleChange('title', e.target.value)}
          />
          <Input
            type='text'
            label='Email'
            placeHolder='name@company.com'
            value={email}
            onChange={(e) => handleChange('email', e.target.value)}
            isDisabled={isDisabled}
          />
        </Form>
        <FormButton>
          <Dropdown
            modalTitle='Access Control'
            isMinWidth={298}
            content={
              <DropTab>
                {getCurrentSelectedRoleIcon}
                <Titles>{getRoleText()}</Titles>
              </DropTab>
            }
            trigger='click'>
            {renderRoleOptions}
          </Dropdown>
          <Button
            title={isEmpty(memberInput?.id) ? 'Add Member' : 'Update'}
            variant={BUTTON_TYPE.PRIMARY}
            buttonType='submit'
            isLoading={loading}
          />
        </FormButton>
      </Bottom>
    </Main>
  );
};

export default observer(AddMemberModal);
